import { generateAvatarInitials } from '../utils/avatar';
import { 
  CONNECTION_STATUSES, 
  DIRECT_LINE_TOKEN_URL, 
  API_ENDPOINTS, 
  SUCCESS_MESSAGES,
  CONSOLE_MESSAGES 
} from '../utils/constants';
import globalConstants from '@/plugins/constants';

export default {
    namespaced: true,
    modules: {},
    state: {
        menu: [
            { product: true, title: globalConstants.ASK_NOA_LABEL, bgcolor: 'primary' },
            {
                title: 'Chat', icon: '/img/icons/ark-agent-icon.svg', href: '/chatbot/home',
            },
        ],
        connectionStatus: null,
        connectionError: null,
        directLineToken: '',
        directLineTenant: '', // Azure AD tenant ID for DirectLine
        tokenContext: null,
        isTokenLoading: false,
        tokenError: null,
        project: null, // used for sending to teams API (future use)
        task: null, // used for sending to teams API (future use)
        filters: null, // used for sending to teams API (future use)
        role: null, // user role in chatbot context
        toolsCatalog: [], // dynamic tools/suggestions from API
        toolsLoading: false,
        toolsError: null,
    },
    mutations: {
        SET_CONNECTION_STATUS(state, status) {
            state.connectionStatus = status;
        },
        SET_CONNECTION_ERROR(state, error) {
            state.connectionError = error;
        },
        SET_DIRECT_LINE_TOKEN(state, { token, userId }) {
            // Decrypt the token using the same method as encryption but in reverse
            function decryptToken(encryptedToken, userId) {
                if (!encryptedToken) return '';
                
                try {
                    // Decode from Base64 (using appropriate method based on environment)
                    let encrypted;
                    if (typeof atob === 'function') {
                        // Browser environment
                        encrypted = atob(encryptedToken);
                    } else if (typeof Buffer !== 'undefined') {
                        // Node.js environment
                        encrypted = Buffer.from(encryptedToken, 'base64').toString();
                    } else {
                        throw new Error('No Base64 decoding method available');
                    }
                    
                    // Create the same encryption key using JWT secret and user ID
                    const encryptionKey = (process.env.VUE_APP_JWT_SECRET_KEY || 'consark-butterfly-secure-key') + '_' + userId;
                    let decrypted = '';
                    
                    // Apply XOR operation to decrypt
                    for (let i = 0; i < encrypted.length; i++) {
                        const charCode = encrypted.charCodeAt(i) ^ encryptionKey.charCodeAt(i % encryptionKey.length);
                        decrypted += String.fromCharCode(charCode);
                    }
                    
                    return decrypted;
                } catch (err) {
                    console.error('Error decrypting token:', err);
                    return '';
                }
            }
            
            // Only attempt to decrypt if we have both a token and a user ID
            if (token && userId) {
                state.directLineToken = decryptToken(token, userId);
            } else {
                state.directLineToken = token || '';
            }
        },
        SET_TOKEN_LOADING(state, isLoading) {
            state.isTokenLoading = isLoading;
        },
        SET_TOKEN_ERROR(state, error) {
            state.tokenError = error;
        },
        SET_PROJECT(state, projectId) {
            state.project = projectId;
        },
        SET_TASK(state, taskId) {
            state.task = taskId;
        },
        SET_FILTERS(state, filters) {
            state.filters = filters;
        },
        SET_ROLE(state, role) {
            state.role = role;
        },
        SET_TOKEN_CONTEXT(state, context) {
            state.tokenContext = context;
        },
        SET_DIRECT_LINE_TOKEN_RAW(state, token) {
            state.directLineToken = token;
        },
        SET_DIRECT_LINE_TENANT(state, tenant) {
            state.directLineTenant = tenant || '';
        },
        SET_TOOLS_CATALOG(state, tools) {
            state.toolsCatalog = tools;
        },
        SET_TOOLS_LOADING(state, isLoading) {
            state.toolsLoading = isLoading;
        },
        SET_TOOLS_ERROR(state, error) {
            state.toolsError = error;
        }
    },
    actions: {
        updateConnectionStatus({ commit }, status) {
            commit('SET_CONNECTION_STATUS', status);
        },
        setConnectionError({ commit }, error) {
            commit('SET_CONNECTION_ERROR', error);
        },
        setProject({ commit }, projectId) {
            commit('SET_PROJECT', projectId);
        },
        setRole({ commit }, role) {
            commit('SET_ROLE', role);
        },
        async fetchDirectLineToken({ commit, state, rootState, rootGetters }) {
            const currentClient = rootState.client;

            // Check if we have a valid token for the current context
            if (state.directLineToken && state.tokenContext) {
                if (state.tokenContext.client === currentClient) {
                    return { status: 'success', token: state.directLineToken };
                }
            }

            try {
                commit('SET_TOKEN_LOADING', true);
                commit('SET_TOKEN_ERROR', null);
                
                const url = '/v2/chatbot/getEnvConfig';
                const response = await rootGetters.axiosObject.post(url);

                if (response?.data?.status === 'success') {
                    // Pass the encrypted token and user ID as an object to the mutation
                    commit('SET_DIRECT_LINE_TOKEN', { 
                        token: response.data.data[0].VUE_APP_CHAT_DIRECT_LINE_TOKEN || '', 
                        userId: rootState.user?.id 
                    });
                    
                    // Store the Azure AD tenant ID for DirectLine
                    commit('SET_DIRECT_LINE_TENANT', response.data.data[0].VUE_APP_CHAT_DIRECT_LINE_TENANT || '');
                    
                    // Get the decrypted token (which might be a Secret or a Token)
                    let currentToken = state.directLineToken;
                    
                    // Try to exchange for a conversation token (in case it's a secret)
                    // This ensures we get a token bound to a conversation
                    try {
                        const genRes = await fetch(DIRECT_LINE_TOKEN_URL, {
                            method: 'POST',
                            headers: { Authorization: `Bearer ${currentToken}` }
                        });
                        
                        if (genRes.ok) {
                            const data = await genRes.json();
                            currentToken = data.token;
                            commit('SET_DIRECT_LINE_TOKEN_RAW', currentToken);
                        }
                    } catch (e) {
                        console.warn('Token generation failed, using original', e);
                    }
                    
                    // Save the context for this token
                    commit('SET_TOKEN_CONTEXT', {
                        client: currentClient,
                        tenant: state.directLineTenant
                    });
                    
                    return { status: 'success', token: currentToken };
                } else {
                    const errorMessage = response?.data?.message || 'Failed to fetch Direct Line token';
                    commit('SET_TOKEN_ERROR', errorMessage);
                    commit('sbError', errorMessage, { root: true });
                    return { status: 'error', message: errorMessage };
                }
            } catch (err) {
                const errorMessage = err?.message || 'Error fetching Direct Line token';
                commit('SET_TOKEN_ERROR', errorMessage);
                commit('sbError', errorMessage, { root: true });
                return { status: 'error', message: errorMessage };
            } finally {
                commit('SET_TOKEN_LOADING', false);
            }
        },
        /**
         * Logout user - handles the logout API call with proper error handling
         * @param {Object} context - Vuex action context
         * @param {Object} context.commit - Vuex commit function
         * @param {Object} context.rootGetters - Root getters for accessing axiosObject
         * @returns {Promise<Object>} - Returns status object {status: 'success'|'error'}
         */
        async logout({ commit, rootGetters }) {
            try {
                const response = await rootGetters.axiosObject.post(API_ENDPOINTS.LOGOUT, {});
                
                if (response.data.status === 'success') {
                    // Clear user state
                    commit('changeLogin', false, { root: true });
                    commit('setUser', null, { root: true });
                    
                    // Show success notification
                    commit('sbSuccess', SUCCESS_MESSAGES.LOGOUT_SUCCESS, { root: true });
                    return { status: 'success' };
                }
                
                return { status: 'error' };
            } catch (error) {
                console.error(CONSOLE_MESSAGES.LOGOUT_ERROR, error);
                // commit('sbError', error.response?.data?.message || 'Logout failed. Please try again.', { root: true });
                return { status: 'error' };
            }
        },
        /**
         * Fetch tools catalog - retrieves available chat tools/suggestions from API
         * @param {Object} context - Vuex action context
         * @param {Object} context.commit - Vuex commit function
         * @param {Object} context.state - Vuex state
         * @param {Object} context.rootGetters - Root getters for accessing axiosObject
         * @returns {Promise<Object>} - Returns status object {status: 'success'|'error', data?: Array}
         */
        async fetchToolsCatalog({ commit, state, rootGetters }) {
            // Return cached data if already loaded
            if (state.toolsCatalog && state.toolsCatalog.length > 0) {
                return { status: 'success', data: state.toolsCatalog };
            }

            try {
                commit('SET_TOOLS_LOADING', true);
                commit('SET_TOOLS_ERROR', null);
                
                const response = await rootGetters.axiosObject.post(API_ENDPOINTS.TOOLS_CATALOG, {});
                
                if (response?.data?.status === 'success' && response?.data?.data?.tools) {
                    // Validate and transform API response to match component expectations
                    const rawTools = response.data.data.tools;
                    
                    // Validate each tool has required fields
                    const validatedTools = rawTools
                        .filter(tool => {
                            // Ensure tool has minimum required fields
                            const hasRequiredFields = tool && 
                                typeof tool.title === 'string' && 
                                typeof tool.prompt === 'string';
                            
                            if (!hasRequiredFields) {
                                console.warn(CONSOLE_MESSAGES.FETCH_TOKEN_ERROR, 'Invalid tool structure:', tool);
                            }
                            
                            return hasRequiredFields;
                        })
                        .map(tool => ({
                            title: tool.title,
                            prompt: tool.prompt,
                            description: tool.description || '',
                            parametersDescription: tool.parametersDescription || [],
                            examples: tool.examples || [],
                            application: String(tool.application || 'common').toLowerCase()
                        }));
                    
                    commit('SET_TOOLS_CATALOG', validatedTools);
                    return { status: 'success', data: validatedTools };
                } else {
                    const errorMessage = response?.data?.message || 'Failed to fetch tools catalog';
                    commit('SET_TOOLS_ERROR', errorMessage);
                    commit('sbError', errorMessage, { root: true });
                    return { status: 'error', message: errorMessage };
                }
            } catch (error) {
                console.error(CONSOLE_MESSAGES.FETCH_TOKEN_ERROR, error);
                const errorMessage = error?.response?.data?.message || 'Error fetching tools catalog';
                commit('SET_TOOLS_ERROR', errorMessage);
                commit('sbError', errorMessage, { root: true });
                return { status: 'error', message: errorMessage };
            } finally {
                commit('SET_TOOLS_LOADING', false);
            }
        }
    },
    getters: {
        isConnected: state => state.connectionStatus === CONNECTION_STATUSES.CONNECTED,
        connectionError: state => state.connectionError,
        directLineTenant: state => state.directLineTenant,
        computedStyleOptions: (state, getters, rootState) => {
            return {
                userAvatarInitials: rootState.user && rootState.user.name
                    ? generateAvatarInitials(rootState.user.name)
                    : ''
            };
        },
        toolsCatalog: state => state.toolsCatalog,
        isToolsLoading: state => state.toolsLoading,
        toolsError: state => state.toolsError
    }
}